import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const inputClassName = 'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'

export const DRIVING_LICENSE_OPTIONS = ['a', 'b', 'c', 'd']
export const EMPLOYMENT_OPTIONS = [
	{ value: 'B2B', labelKey: 'b2b' },
	{ value: 'Umowa zlecenie', labelKey: 'umowaZlecenie' },
	{ value: 'Umowa o pracę', labelKey: 'umowaOPrace' },
]
export const EDUCATION_OPTIONS = ['podstawowe', 'srednie', 'wyzsze']

export const emptyTrainerExtendedFields = {
	prawo_jazdy: [],
	zatrudnienie: '',
	RODO: '',
	KRK: '',
	RSTPS: '',
	wyksztalcenie: '',
	dowod_osobisty: '',
	PESEL: '',
	pelna_teczka: false,
	notatka: '',
}

const toggleArrayValue = (values, option, checked) => {
	const current = Array.isArray(values) ? values : []
	if (checked) {
		return current.includes(option) ? current : [...current, option]
	}
	return current.filter(item => item !== option)
}

export default function UserTrainerExtendedFields({ form, onChange }) {
	const { t } = useTranslation()

	const handleLicenseToggle = useCallback((option, checked) => {
		onChange({ prawo_jazdy: toggleArrayValue(form.prawo_jazdy, option, checked) })
	}, [form.prawo_jazdy, onChange])

	return (
		<div className="rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<h2 className="text-sm font-semibold text-gray-900">
				{t('settingsPage.users.extendedModule.title')}
			</h2>
			<p className="mt-1 text-sm text-gray-500">
				{t('settingsPage.users.extendedModule.description')}
			</p>

			<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="md:col-span-2">
					<span className="text-sm font-medium text-gray-700">
						{t('settingsPage.users.fields.drivingLicense')}
					</span>
					<div className="mt-2 flex flex-wrap gap-3">
						{DRIVING_LICENSE_OPTIONS.map(option => (
							<label key={option} className="flex items-center gap-2 text-sm text-gray-700">
								<input
									type="checkbox"
									checked={Array.isArray(form.prawo_jazdy) && form.prawo_jazdy.includes(option)}
									onChange={event => handleLicenseToggle(option, event.target.checked)}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
								/>
								{t(`settingsPage.users.drivingLicenseOptions.${option}`)}
							</label>
						))}
					</div>
				</div>

				<div className="md:col-span-2">
					<span className="text-sm font-medium text-gray-700">
						{t('settingsPage.users.fields.employment')}
					</span>
					<div className="mt-2 flex flex-wrap gap-3">
						{EMPLOYMENT_OPTIONS.map(option => (
							<label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
								<input
									type="radio"
									name="zatrudnienie"
									value={option.value}
									checked={form.zatrudnienie === option.value}
									onChange={() => onChange({ zatrudnienie: option.value })}
									className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
								/>
								{t(`settingsPage.users.employmentOptions.${option.labelKey}`)}
							</label>
						))}
					</div>
				</div>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.rodo')}</span>
					<input
						type="date"
						value={form.RODO || ''}
						onChange={event => onChange({ RODO: event.target.value })}
						className={inputClassName}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.krk')}</span>
					<input
						type="date"
						value={form.KRK || ''}
						onChange={event => onChange({ KRK: event.target.value })}
						className={inputClassName}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.rstps')}</span>
					<input
						type="date"
						value={form.RSTPS || ''}
						onChange={event => onChange({ RSTPS: event.target.value })}
						className={inputClassName}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.education')}</span>
					<select
						value={form.wyksztalcenie || ''}
						onChange={event => onChange({ wyksztalcenie: event.target.value })}
						className={inputClassName}
					>
						<option value="">{t('settingsPage.users.educationOptions.empty')}</option>
						{EDUCATION_OPTIONS.map(option => (
							<option key={option} value={option}>
								{t(`settingsPage.users.educationOptions.${option}`)}
							</option>
						))}
					</select>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.idDocument')}</span>
					<input
						type="text"
						value={form.dowod_osobisty || ''}
						onChange={event => onChange({ dowod_osobisty: event.target.value })}
						placeholder={t('settingsPage.users.placeholders.idDocument')}
						className={inputClassName}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.pesel')}</span>
					<input
						type="text"
						value={form.PESEL || ''}
						onChange={event => onChange({ PESEL: event.target.value })}
						placeholder={t('settingsPage.users.placeholders.pesel')}
						inputMode="numeric"
						maxLength={11}
						className={inputClassName}
					/>
				</label>

				<label className="flex items-center gap-2 text-sm text-gray-700 md:col-span-2">
					<input
						type="checkbox"
						checked={Boolean(form.pelna_teczka)}
						onChange={event => onChange({ pelna_teczka: event.target.checked })}
						className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
					/>
					{t('settingsPage.users.fields.fullFolder')}
				</label>

				<label className="flex flex-col gap-1 md:col-span-2">
					<span className="text-sm font-medium text-gray-700">{t('settingsPage.users.fields.note')}</span>
					<textarea
						value={form.notatka || ''}
						onChange={event => onChange({ notatka: event.target.value })}
						placeholder={t('settingsPage.users.placeholders.note')}
						rows={3}
						className={inputClassName}
					/>
				</label>
			</div>
		</div>
	)
}
